import type {
    FC,
    InputHTMLAttributes,
    ReactNode,
    SelectHTMLAttributes,
    TextareaHTMLAttributes
} from 'react'

import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface FieldProps {
    id: string
    label: string
    hint?: string
    error?: string
    optional?: boolean
    className?: string
    children: ReactNode
}

// Label, control, hint and error stacked with consistent spacing
export const Field: FC<FieldProps> = ({
    id,
    label,
    hint,
    error,
    optional = false,
    className,
    children
}): ReactNode => {
    return (
        <div className={cn('flex flex-col gap-2', className)}>
            <label htmlFor={id} className='flex items-baseline justify-between gap-3 text-[0.8125rem] font-medium text-charcoal'>
                <span>
                    {label}
                </span>

                {optional && (
                    <span className='text-xs font-normal text-muted-foreground'>
                        Optional
                    </span>
                )}
            </label>

            {children}

            {hint && !error && (
                <p id={`${id}-hint`} className='text-xs leading-relaxed text-muted-foreground'>
                    {hint}
                </p>
            )}

            {error && (
                <p id={`${id}-error`} className='text-xs leading-relaxed text-destructive'>
                    {error}
                </p>
            )}
        </div>
    )
}

export const controlClasses = cn(
    'w-full rounded-xl border border-input bg-cream px-4 text-[0.9375rem] text-charcoal transition-[border-color,box-shadow] duration-200',
    'placeholder:text-charcoal/40 hover:border-charcoal/30',
    'focus:border-forest focus:outline-none focus:ring-4 focus:ring-forest/10',
    'aria-[invalid=true]:border-destructive disabled:cursor-not-allowed disabled:opacity-60'
)

export const TextInput: FC<InputHTMLAttributes<HTMLInputElement>> = ({ className, ...props }): ReactNode => {
    return <input className={cn(controlClasses, 'h-12', className)} {...props} />
}

export const TextArea: FC<TextareaHTMLAttributes<HTMLTextAreaElement>> = ({ className, ...props }): ReactNode => {
    return <textarea className={cn(controlClasses, 'min-h-28 resize-y py-3 leading-relaxed', className)} {...props} />
}

export const SelectInput: FC<SelectHTMLAttributes<HTMLSelectElement>> = ({ className, children, ...props }): ReactNode => {
    return (
        <div className='relative'>
            <select className={cn(controlClasses, 'h-12 cursor-pointer appearance-none pr-11', className)} {...props}>
                {children}
            </select>

            <ChevronDown aria-hidden='true' className='pointer-events-none absolute right-4 top-1/2 size-4 -translate-y-1/2 text-charcoal/50' />
        </div>
    )
}

interface FormMessageProps {
    tone: 'error' | 'success' | 'info'
    children: ReactNode
    className?: string
}

const messageTones = {
    error: 'border-destructive/25 bg-destructive/5 text-destructive',
    success: 'border-forest/20 bg-forest-soft text-forest',
    info: 'border-stone bg-sand text-charcoal/80'
}

// Inline status message announced politely to screen readers
export const FormMessage: FC<FormMessageProps> = ({ tone, children, className }): ReactNode => {
    return (
        <div role={tone === 'error' ? 'alert' : 'status'} className={cn('rounded-xl border px-4 py-3 text-sm leading-relaxed', messageTones[tone], className)}>
            {children}
        </div>
    )
}
